import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { FormProvider, useForm } from "react-hook-form";
import { useQuery } from "urql";

import { metaDefault } from "../../config/meta";
import { CHECK_GIFTCARD_QUERY, RECAPTCHA_VERIFY_QUERY } from "../../graphql/queries";
import { getString } from "../../utilities";
import { routeCodes } from "../../config/routes";
import { recaptchaSiteKey } from "../../config/constants";
import { validateGiftCardOrPromo } from "../../utilities/forms/rules";
import { useLazyQuery, useQueryHandlers } from "../../hooks";
import OptionallyDisplayed from "../global/optionallyDisplayed";
import LoadingAnim from "../global/loadingAnim";
import ErrorMessage from "../global/errorMessage";
import { freePublicUser } from "../../utilities";
import RedemptionForm from "../global/RedemptionForm";

export default function Redeem() {
  const action = `Check_GiftCard_Form_Submission`;
  const [querying, setQuerying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState(null);
  const { formState, handleSubmit, register, getValues } = useForm({
    mode: "onChange",
    defaultValues: { code: "" },
  });

  const onError = (error) => {
    setQuerying(false);
    setSubmitting(false);
    setSubmitErrors(error);
  };

  const [giftCard, setGiftCard] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  const applyGiftCard = (data) => {
    if (data && data.checkGiftCard) {
      const { checkGiftCard } = data;

      if (checkGiftCard.redeemed) {
        // already redeemed
        // supply error message {message: "my message"}
        setSubmitErrors({ message: getString("giftcard.errors.redeemed") });
        setQuerying(false);
      } else {
        // not redeemed - perform duties
        setQuerying(false);

        navigate(routeCodes.REDEEMCARD, { state: { checkGiftCard } });
      }
    } else {
      // came back null (no such giftcard in database)
      // supply error message
      setSubmitErrors({ message: getString("giftcard.errors.invalid") });
      setQuerying(false);
    }
  };

  const [result] = useQuery({
    query: CHECK_GIFTCARD_QUERY,
    variables: { id: giftCard },
    pause: !giftCard,
  });
  useQueryHandlers(result, (data) => applyGiftCard(data), onError);

  const checkGiftcardHandler = () => {
    if (!querying) {
      setSubmitting(false);
      setQuerying(true);
      setSubmitErrors(null);

      const giftCard = getValues("code");
      setGiftCard(giftCard);
    }
  };

  const onCompleted = (data) => {
    const { reCaptchaVerify } = data;

    if (reCaptchaVerify.success) {
      // verified
      checkGiftcardHandler();
    } else {
      // not verified
      setSubmitting(false);
      setSubmitErrors({ message: reCaptchaVerify.message });
    }
  };

  const { executeQuery: recaptcha, loading: recaptchaLoading } = useLazyQuery(
    RECAPTCHA_VERIFY_QUERY,
    onCompleted,
    onError,
  );

  const recaptchaVerify = () => {
    if (!submitting) {
      setSubmitting(true);

      if (freePublicUser()) {
        // perform reCaptcha to get token
        window.grecaptcha.enterprise.ready(async () => {
          const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action });

          if (token) {
            recaptcha({ token, action, verify: false, siteKey: recaptchaSiteKey });
          }
        });
      } else {
        checkGiftcardHandler();
      }
    }
  };

  return (
    <>
      {metaDefault({ path: location.pathname, title: getString("redeem.giftcard.title") })}

      <FormProvider handleSubmit={handleSubmit} formState={formState} register={register} getValues={getValues}>
        <RedemptionForm
          onSubmit={recaptchaVerify}
          submitErrors={submitErrors}
          validationRules={{
            required: getString("forms.errorMessages.isRequired.0", { replace: [getString("redeem.code")] }),
            validate: (value) =>
              value.length === 0 || validateGiftCardOrPromo(value) || getString("giftcard.errors.invalid"),
          }}
          isLoading={recaptchaLoading || querying || submitting}
          loadMessage={getString("giftcard.checking")}
          redeemCta={getString("redeem.code")}
          redeemLabel={getString("redeem.giftcard.label")}
          placeholder={getString("giftcard.claim.example")}
          buttonText={getString("redeem.button")}
          title={getString("redeem.giftcard.title")}
        />
      </FormProvider>
    </>
  );
}
